/**
 * Trash helpers — which descendants a soft delete takes along, and which a restore brings back.
 *
 * Checklist rows live under `${task.parentPath}/${task.id}`, and the user can delete one on its
 * own. Trashing and then restoring the task must not bring such a row back. deletedAt tells the
 * two apart: a cascade stamps every row it takes with the trashed object's own deletedAt, and that
 * stamp is chosen so that no row deleted on its own carries it — the clock alone does not
 * guarantee that (two deletes in one millisecond, or a Durable Object clock that repeats). The
 * other way round too: deleteChecklistItem stamps a row so that no trashed object above it
 * carries the row's stamp, also when the row is already in the trash with its task.
 *
 * INTERNAL — not exported from operations/index.ts.
 */

import type { IndexableType, Table } from "dexie"
import { ObjectType } from "../db-types.js"
import type { DbObject } from "../db-types.js"
import { getDb } from "../runtime.js"

/**
 * Every object below `obj`: its checklist rows, a container's tasks and their rows, …
 *
 * A task's rows live at `<its parentPath>/<id>`. A container's children live at `root/<id>` wherever the
 * container itself sits: a project inside an area has `root/<areaId>` as parentPath but its tasks are at
 * `root/<projectId>`, which `<parentPath>/<id>` does not match. So for a project, list or area the
 * children of every project / list found below it are read as well (an area holds projects whose
 * tasks are one level further down).
 */
export async function loadDescendants(obj: DbObject): Promise<DbObject[]> {
  const totodo = getDb().totodo
  const found = await totodo.where("parentPath").startsWith(`${obj.parentPath}/${obj.id}`).toArray()
  if (obj.type !== ObjectType.Project && obj.type !== ObjectType.List && obj.type !== ObjectType.Area) {
    return found
  }
  const seen = new Set(found.map((d) => d.id))
  const containers = [obj, ...found.filter((d) => d.type === ObjectType.Project || d.type === ObjectType.List)]
  for (const container of containers) {
    const path = `root/${container.id}`
    if (container.type === ObjectType.Area || path === `${obj.parentPath}/${obj.id}`) continue // already read above
    const own = await totodo.where("parentPath").startsWith(path).toArray()
    for (const d of own) {
      if (seen.has(d.id)) continue
      seen.add(d.id)
      found.push(d)
    }
  }
  return found
}

/**
 * The deleted objects a trash stamped at `now` can collide with: uniqueDeletedAt only looks at
 * deletedAt values from `now` on. One indexed read, usually empty, for any number of objects
 * trashed at once — instead of loading each one's descendants, live ones included. Filter it
 * per object with isDescendantOf. (deletedAt is a millisecond string, so its string order is
 * its time order.)
 */
export function loadDeletedSince(now: string): Promise<DbObject[]> {
  return getDb().totodo.where("deletedAt").aboveOrEqual(now).toArray()
}

/** Is `d` below `obj`, as loadDescendants finds it? */
export function isDescendantOf(d: DbObject, obj: DbObject): boolean {
  return typeof d.parentPath === "string" && d.parentPath.startsWith(`${obj.parentPath}/${obj.id}`)
}

/**
 * What trashing an object cascades to: every live descendant, every non-checklist one, and the
 * checklist rows that went to the trash earlier together with their (since trashed) task — they
 * carry that task's deletedAt — so they come back when the task does. A checklist row the user
 * deleted on its own stays as it is.
 */
export function trashCascade(descendants: readonly DbObject[]): DbObject[] {
  const trashedBefore = new Map<string, string | undefined>()
  for (const d of descendants) {
    if (d.isDeleted === 1 && d.type !== ObjectType.Checklist) {
      trashedBefore.set(`${d.parentPath}/${d.id}`, d.deletedAt)
    }
  }
  return descendants.filter(
    (d) =>
      d.isDeleted !== 1 ||
      d.type !== ObjectType.Checklist ||
      (trashedBefore.has(d.parentPath) &&
        trashedBefore.get(d.parentPath) === d.deletedAt),
  )
}

/**
 * The deletedAt for trashing an object at `now`: `now`, or the first millisecond after it that no
 * deleted descendant outside `cascaded` (the rows the delete leaves alone) already carries.
 * Without this, a row deleted on its own in the same millisecond as its task would look like
 * part of the task's cascade and come back on restore. deleteChecklistItem passes the trashed
 * objects above the row instead, for the row's own stamp.
 */
export function uniqueDeletedAt(
  now: string,
  descendants: readonly DbObject[],
  cascaded: readonly DbObject[] = [],
): string {
  const restamped = new Set(cascaded)
  const taken = new Set<string>()
  for (const d of descendants) {
    if (d.isDeleted === 1 && d.deletedAt && !restamped.has(d)) taken.add(d.deletedAt)
  }
  let ms = Number(now)
  if (!Number.isFinite(ms)) return now
  while (taken.has(String(ms))) ms++
  return String(ms)
}

/**
 * What restoring `obj` brings back: every trashed non-checklist descendant, and with each object
 * that comes back (`obj` itself or such a descendant) the checklist rows its own delete took —
 * the rows under it whose deletedAt equals its deletedAt — exactly as restoreFromTrash of that
 * object alone would. So a task restored with its container gets its checklist back whether it
 * was trashed with the container (deleteTask restamps it and its rows) or on its own before
 * (its rows carry its own stamp, e.g. after the app's deleteProjectWithChildren, which stamps
 * only the project and its live children). A row the user deleted on its own stays deleted.
 * An object without deletedAt (legacy) has nothing to compare against, so all its rows; a row
 * whose task does not come back is compared against `obj`, as before.
 */
export function restoreCascade(
  obj: DbObject,
  descendants: readonly DbObject[],
): DbObject[] {
  // Checklist path → deletedAt of the object that owns it and comes back in this restore
  const restoredOwners = new Map<string, string | undefined>([
    [`${obj.parentPath}/${obj.id}`, obj.deletedAt],
  ])
  for (const d of descendants) {
    if (d.isDeleted === 1 && d.type !== ObjectType.Checklist) {
      restoredOwners.set(`${d.parentPath}/${d.id}`, d.deletedAt)
    }
  }
  return descendants.filter((d) => {
    if (d.isDeleted !== 1) return false
    if (d.type !== ObjectType.Checklist) return true
    const stamp = restoredOwners.has(d.parentPath)
      ? restoredOwners.get(d.parentPath)
      : obj.deletedAt
    return !stamp || d.deletedAt === stamp
  })
}

// ---------------------------------------------------------------------------
// Permanent removal
// ---------------------------------------------------------------------------

/** How many objects one removal step handles: bounds the keys held in memory and one transaction. */
export const PURGE_CHUNK = 500

const DOC_UPDATES_TABLE = "$totodo.doc_updates"

/**
 * The primary keys of the rows of `table` whose `index` value is one of `values`: one key-only
 * indexed lookup per value, never a row. `where(index).anyOf(values)` is the wrong tool on
 * dexie-sqlite: it reads the whole range between the smallest and the largest value with every row's
 * value, and ids are random, so that is about the whole table. (A key pass over the whole index
 * would need `table.count()`, a full COUNT(*) scan there, to decide when it is cheaper - so no.)
 */
async function keysOfIndexValues<T, K extends IndexableType>(
  table: Table<T, K>,
  index: string,
  values: readonly string[],
): Promise<K[]> {
  const found: K[] = []
  for (const value of values) found.push(...(await table.where(index).equals(value).primaryKeys()))
  return found
}

/**
 * Permanently remove `objects` and everything that hangs on them: the checklist rows below each one
 * (`<parentPath>/<id>`, live ones too - a delete leaves a task's rows live under it so that a restore
 * brings them back, so nothing but this takes them away), and for the objects and those rows alike
 * the schedule row (`#<id>`), the reminders and the Y.Doc updates of the document.
 *
 * Only keys are read to find all this (see keysOfIndexValues) and every delete is one bulk delete by
 * key per table. Done in steps of PURGE_CHUNK objects, one transaction each - the keys are read
 * inside it, so a row, reminder or document update that arrives (sync) while a step runs is either
 * in the step or untouched, never orphaned - so a large trash is neither held in memory nor written
 * in one go. Returns the number of checklist rows removed besides
 * `objects`. Objects without a row (already gone) are cleaned up as far as their id goes.
 */
export async function purgeObjects(
  objects: ReadonlyArray<Pick<DbObject, "id" | "parentPath">>,
): Promise<number> {
  const db = getDb()
  const docUpdates = db.tables.find((t) => t.name === DOC_UPDATES_TABLE)
  let rowsRemoved = 0

  for (let i = 0; i < objects.length; i += PURGE_CHUNK) {
    const chunk = objects.slice(i, i + PURGE_CHUNK).filter((o): o is { id: string; parentPath: string } => !!o.id)
    if (chunk.length === 0) continue
    const ids = new Set(chunk.map((o) => o.id))

    rowsRemoved += await db.transaction(
      "rw",
      docUpdates ? [db.totodo, db.startAtIndex, db.reminder, docUpdates] : [db.totodo, db.startAtIndex, db.reminder],
      async () => {
        // The checklist rows below these objects (not ones already part of this removal): those under
        // one object share one [parentPath+type] key, so one key-only indexed read per object
        const rowIds = new Set<string>()
        for (const o of chunk) {
          if (typeof o.parentPath !== "string") continue
          const keys = await db.totodo
            .where("[parentPath+type]")
            .equals([`${o.parentPath}/${o.id}`, ObjectType.Checklist])
            .primaryKeys()
          for (const key of keys) if (key && !ids.has(key)) rowIds.add(key)
        }
        const allIds = [...ids, ...rowIds]

        const reminderKeys = await keysOfIndexValues(db.reminder, "taskId", allIds)
        const updateKeys = docUpdates ? await keysOfIndexValues(docUpdates, "k", allIds) : []
        // Only scheduled objects have a schedule row: look the keys up in batches, delete what is there
        const scheduleOf = allIds.map((id) => `#${id}`)
        const scheduleRows = await db.startAtIndex.bulkGet(scheduleOf)
        const scheduleKeys = scheduleOf.filter((_, n) => !!scheduleRows[n])

        await db.startAtIndex.bulkDelete(scheduleKeys)
        await db.reminder.bulkDelete(reminderKeys)
        if (docUpdates) await docUpdates.bulkDelete(updateKeys)
        await db.totodo.bulkDelete(allIds)
        return rowIds.size
      },
    )
  }
  return rowsRemoved
}
